import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MarkdownRender, ArtifactIframe, LinkArtifactView } from "@/pages/ModuleEditor";
import { sanitizeProseHtml } from "@/lib/sanitize";
import LearningCompanion from "@/components/LearningCompanion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  Trophy, CheckCircle2, XCircle, ArrowRight, Sparkles, BookOpen,
  ListChecks, Loader2, ArrowLeft, RotateCcw, ChevronRight, Target, MessageSquare
} from "lucide-react";

// Pre-scripted Aria lines — "excited friend" voice, max 22 words each.
const ARIA_LINES = {
  intro: [
    "Let's crack this one! 🔥 I'll cheer you on the whole way.",
    "Fresh module — fresh wins. I'm reading along with you.",
    "Strap in. Let's make this content stick.",
  ],
  q25: ["A quarter down — great pace!", "You're flying. Keep that rhythm.", "Nice momentum. Don't stop now."],
  q50: ["Halfway! The juicy stuff is coming.", "🎯 Halfway mark — strongest part is next.", "You're on fire. Halfway!"],
  q75: ["Almost there. The quiz is gonna feel easy.", "75% — final stretch!", "Smell that? That's the finish line."],
  done: ["💥 You crushed it. Quiz time?", "Done and dusted! Let's ace that quiz.", "Boom. Ready to prove it?"],
  quizCorrect: ["YES! ✨", "Nailed it!", "🔥 Exactly.", "Boom!", "Effortless."],
  quizWrong: ["Close — you've got this.", "Shake it off. Next one's yours.", "Don't sweat it. Keep going."],
  quizPass: ["🎉 LEGEND. Quiz aced!", "🏆 Module conquered. Onto the next!", "First-try energy. Beautiful."],
  quizFail: ["Almost! Review and try again.", "So close. One more lap?", "You're nearly there — give it another shot."],
};

const pickLine = (key) => {
  const arr = ARIA_LINES[key] || [""];
  return arr[Math.floor(Math.random() * arr.length)];
};

/**
 * Full reading + quiz experience for a module.
 * - Sticky top header with section breadcrumbs + progress bar
 * - Auto-completion detection (95% scroll or "I'm done" button)
 * - Confetti on completion
 * - Quiz takeover mode (artifact hidden during quiz)
 * - Score submission via /api/progress
 */
export default function ReadingExperience({ mod, learner, onClose }) {
  const [stage, setStage] = useState("reading"); // reading | quiz | result
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(null);
  const [sections, setSections] = useState([]); // [{id, label, level, el}]
  const [completed, setCompleted] = useState(false);
  // Set of section ids the learner has reached/passed — drives the tick UI.
  const [seenSections, setSeenSections] = useState(() => new Set());
  // Last-known persisted scroll pct (from server) — used to restore position.
  const [resumePct, setResumePct] = useState(0);
  const [showResumeToast, setShowResumeToast] = useState(false);
  // Whether the floating section checklist is collapsed by the learner.
  const [tickerOpen, setTickerOpen] = useState(true);

  const scrollRef = useRef(null);
  const proseRef = useRef(null);
  const ariaRef = useRef(null);
  const milestonesHitRef = useRef(new Set()); // tracks which scroll milestones we've fired
  const lastTipAtRef = useRef(0);              // throttle AI tips (max 1 per minute)
  const sectionEnterTimeRef = useRef(Date.now());
  const persistTimerRef = useRef(null);
  const restoredScrollRef = useRef(false);
  const hwScrollRef = useRef(0); // high-watermark scroll pct (never regress)

  const isIframe = mod.artifact_type === "embed" || mod.artifact_type === "jsx";
  const isLink = mod.artifact_type === "link";

  // Aria intro on module mount
  useEffect(() => {
    const t = setTimeout(() => ariaRef.current?.say(pickLine("intro"), "cheering"), 900);
    return () => clearTimeout(t);
  }, [mod.id]);

  // Scroll-milestone reactions (25/50/75/100)
  useEffect(() => {
    const checkpoints = [
      { pct: 25, key: "q25", mood: "cheering" },
      { pct: 50, key: "q50", mood: "cheering" },
      { pct: 75, key: "q75", mood: "cheering" },
    ];
    for (const c of checkpoints) {
      if (progress >= c.pct && !milestonesHitRef.current.has(c.key)) {
        milestonesHitRef.current.add(c.key);
        ariaRef.current?.say(pickLine(c.key), c.mood);
      }
    }
  }, [progress]);

  // Aria reacts when the learner completes the reading
  useEffect(() => {
    if (completed && !milestonesHitRef.current.has("done")) {
      milestonesHitRef.current.add("done");
      ariaRef.current?.say(pickLine("done"), "celebrating");
    }
  }, [completed]);

  // Dwell-based AI tip — fires when learner sits on the same section >35s,
  // with a 60s cooldown so Aria never feels naggy.
  useEffect(() => {
    if (isLink || stage !== "reading") return undefined;
    sectionEnterTimeRef.current = Date.now();
    const dwellTimer = setTimeout(async () => {
      if (Date.now() - lastTipAtRef.current < 60_000) return;
      // Find the text of the currently active section
      let heading = "";
      let body = "";
      if (sections.length > 0 && activeSection) {
        const sect = sections.find((s) => s.id === activeSection);
        if (sect?.el) {
          heading = sect.el.textContent || "";
          // Gather a few siblings of body text after the heading
          let nx = sect.el.nextElementSibling;
          let bag = [];
          let count = 0;
          while (nx && count < 6 && !["H1", "H2", "H3"].includes(nx.tagName)) {
            const t = (nx.textContent || "").trim();
            if (t) bag.push(t);
            nx = nx.nextElementSibling;
            count++;
          }
          body = bag.join("\n");
        }
      } else if (proseRef.current) {
        body = (proseRef.current.textContent || "").slice(0, 1200);
      }
      if (!body && !heading) return;
      ariaRef.current?.setMood("thinking");
      try {
        const { data } = await api.post("/companion/tip", {
          module_id: mod.id,
          section_heading: heading,
          section_text: body,
        });
        if (data?.tip) {
          lastTipAtRef.current = Date.now();
          ariaRef.current?.say(data.tip, "encouraging", { hideAfter: 9000, holdMoodFor: 4000 });
        } else {
          ariaRef.current?.setMood("idle");
        }
      } catch {
        ariaRef.current?.setMood("idle");
      }
    }, 35_000);
    return () => clearTimeout(dwellTimer);
  }, [activeSection, mod.id, isLink, stage, sections]);

  // ===== Interactive-activity alert =====
  // Scans the rendered prose for interactive widgets (buttons, links, details,
  // form controls, sandboxed iframes / Claude artifacts) and has Aria pop in
  // with a friendly heads-up the first time each one enters viewport AND the
  // learner doesn't interact within 3.5s. Caps at 3 hints per module to avoid spam.
  useEffect(() => {
    if (stage !== "reading") return undefined;
    const prose = proseRef.current;
    if (!prose) return undefined;
    // Slight delay so freshly-rendered HTML has settled
    let cancelled = false;
    const interactiveSelector = [
      "button",
      "a[href]:not([href=''])",
      "details",
      "summary",
      "input:not([type='hidden'])",
      "select",
      "textarea",
      "iframe",
      "[role='button']",
      "[onclick]",
      "[data-uvc-interactive]",
    ].join(",");

    // (`hintFor` helper removed in iter 24 — we no longer show speech bubbles
    // for interactive sections; Aria just pulses a red dot on her avatar.)

    const interacted = new WeakSet();
    const hinted = new WeakSet();
    let hintsGiven = 0;
    const MAX_HINTS = 3;
    const pendingTimers = new Map();

    const armHint = (el) => {
      if (hinted.has(el) || interacted.has(el)) return;
      if (hintsGiven >= MAX_HINTS) return;
      if (pendingTimers.has(el)) return;
      // Blocks the admin explicitly tagged should fire near-instantly;
      // auto-detected widgets get the 3.5s grace window so we don't yell at
      // learners who interact right away.
      const delay = el.hasAttribute?.("data-uvc-interactive") ? 250 : 3500;
      const t = setTimeout(() => {
        pendingTimers.delete(el);
        if (interacted.has(el) || hinted.has(el) || hintsGiven >= MAX_HINTS) return;
        hinted.add(el);
        hintsGiven += 1;
        // Per iter-24 user request: Aria stays in her home corner. No more
        // float-out, no chat bubble, no page-jumping scrollIntoView. The
        // alert is a subtle red dot + extra pulse on her avatar only.
        el.setAttribute("data-uvc-interactive-hint", "1");
        ariaRef.current?.nudge?.();
        // Auto-clear the visual highlight after 6s
        setTimeout(() => { try { el.removeAttribute("data-uvc-interactive-hint"); } catch { /* ignore */ } }, 6000);
      }, delay);
      pendingTimers.set(el, t);
    };
    const cancelHint = (el) => {
      const t = pendingTimers.get(el);
      if (t) { clearTimeout(t); pendingTimers.delete(el); }
    };

    let io;
    const setupTimer = setTimeout(() => {
      if (cancelled) return;
      const nodes = Array.from(prose.querySelectorAll(interactiveSelector));
      if (nodes.length === 0) return;
      nodes.forEach((n) => {
        const onInteract = () => {
          interacted.add(n);
          cancelHint(n);
          n.removeAttribute("data-uvc-interactive-hint");
        };
        n.addEventListener("mousedown", onInteract, { passive: true });
        n.addEventListener("touchstart", onInteract, { passive: true });
        n.addEventListener("focusin", onInteract, { passive: true });
      });
      io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio > 0.4) armHint(e.target);
          else cancelHint(e.target);
        }
      }, { threshold: [0, 0.4, 0.8] });
      nodes.forEach((n) => io.observe(n));
    }, 1200);

    return () => {
      cancelled = true;
      clearTimeout(setupTimer);
      pendingTimers.forEach((t) => clearTimeout(t));
      pendingTimers.clear();
      if (io) io.disconnect();
    };
  }, [mod.id, stage, isLink]);

  // Build section list from rendered DOM (HTML/Markdown only)
  useEffect(() => {
    if (isIframe || isLink) {
      setSections([]);
      return;
    }
    const wait = setTimeout(() => {
      const root = proseRef.current;
      if (!root) return;
      const nodes = Array.from(root.querySelectorAll("h1, h2, h3"));
      const built = nodes.map((el, i) => {
        const label = el.textContent || `Section ${i + 1}`;
        const id = `sect-${i}`;
        el.id = id;
        return { id, label: label.length > 60 ? label.slice(0, 58) + "…" : label, level: parseInt(el.tagName[1], 10), el };
      });
      setSections(built);
      if (built[0]) setActiveSection(built[0].id);
    }, 400);
    return () => clearTimeout(wait);
  }, [mod.artifact_content, mod.artifact_type, isIframe]);

  // Tick prose sections off as they scroll past the lower-third viewport
  // line — same UX as the iframe sections handled via postMessage above.
  useEffect(() => {
    if (isIframe || isLink || sections.length === 0) return undefined;
    const root = scrollRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver((entries) => {
      setSeenSections((prev) => {
        let next = prev;
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const id = e.target.id;
          if (!id || next.has(id)) continue;
          if (next === prev) next = new Set(prev);
          next.add(id);
        }
        return next;
      });
    }, { root, rootMargin: "0px 0px -55% 0px", threshold: 0.01 });
    sections.forEach((s) => s.el && io.observe(s.el));
    return () => io.disconnect();
  }, [sections, isIframe, isLink]);

  // Scroll tracking (for native HTML/Markdown)
  useEffect(() => {
    if (isIframe || isLink) return undefined;
    const onScroll = () => {
      const el = scrollRef.current;
      if (!el) return;
      const total = el.scrollHeight - el.clientHeight;
      const pct = total > 0 ? Math.min(100, (el.scrollTop / total) * 100) : 100;
      setProgress(pct);
      if (pct >= 95 && !completed) setCompleted(true);

      // Detect active section
      if (sections.length > 0) {
        const scrolled = el.scrollTop + 120;
        let current = sections[0]?.id;
        for (const s of sections) {
          if (s.el && s.el.offsetTop <= scrolled) current = s.id;
          else break;
        }
        if (current && current !== activeSection) setActiveSection(current);
      }
    };
    const el = scrollRef.current;
    el?.addEventListener("scroll", onScroll);
    onScroll();
    return () => el?.removeEventListener("scroll", onScroll);
  }, [sections, activeSection, completed, isIframe]);

  // ===== Load saved reading-progress on mount =====
  // Restores the high-watermark scroll pct + previously ticked sections.
  useEffect(() => {
    if (isLink || !mod?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/reading-progress/${mod.id}`);
        if (cancelled) return;
        const pct = Number(data?.scroll_pct) || 0;
        hwScrollRef.current = pct;
        setResumePct(pct);
        if (pct >= 5 && pct < 95) setShowResumeToast(true);
        const completedIds = (data?.sections || [])
          .filter((s) => s.completed)
          .map((s) => s.id);
        if (completedIds.length) setSeenSections(new Set(completedIds));
      } catch (e) {
        /* first-time learner — no saved progress yet, that's fine */
      }
    })();
    return () => { cancelled = true; };
  }, [mod?.id, isLink]);

  // ===== Restore scroll position once the artifact is ready =====
  useEffect(() => {
    if (restoredScrollRef.current || resumePct < 5 || resumePct >= 95) return;
    // Wait for sections/iframe to settle, then jump
    const t = setTimeout(() => {
      if (!isIframe && scrollRef.current) {
        const el = scrollRef.current;
        const total = el.scrollHeight - el.clientHeight;
        if (total > 0) {
          el.scrollTop = (resumePct / 100) * total;
          restoredScrollRef.current = true;
        }
      }
      // For iframe we can't directly set its scrollTop from outside (cross-doc),
      // so we surface the resume toast and let the learner choose to jump.
      if (isIframe) restoredScrollRef.current = true;
    }, isIframe ? 1500 : 600);
    return () => clearTimeout(t);
  }, [resumePct, isIframe, sections.length]);

  // ===== Debounced persistence of progress to server =====
  useEffect(() => {
    if (isLink || stage !== "reading" || !mod?.id) return undefined;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      const pct = Math.max(hwScrollRef.current, progress);
      hwScrollRef.current = pct;
      const payload = {
        module_id: mod.id,
        course_id: mod.course_id || null,
        scroll_pct: pct,
        sections: sections.map((s) => ({
          id: s.id,
          label: s.label || "",
          completed: seenSections.has(s.id),
        })),
      };
      api.post("/reading-progress", payload).catch(() => {});
    }, 1500);
    return () => persistTimerRef.current && clearTimeout(persistTimerRef.current);
  }, [progress, seenSections, sections, mod?.id, mod?.course_id, isLink, stage]);

  // ===== Engaged-time tracking (admin analytics) =====
  // Counts seconds while the tab is visible and the learner interacted (or
  // scrolled) within the last 2 minutes; flushed as active_seconds deltas.
  const activeSecondsRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  useEffect(() => { lastActivityRef.current = Date.now(); }, [progress, seenSections]);
  useEffect(() => {
    if (isLink || stage !== "reading" || !mod?.id) return undefined;
    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousemove", "keydown", "click", "touchstart", "scroll", "wheel"];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    const flushDelta = () => {
      const secs = Math.round(activeSecondsRef.current);
      if (secs < 5) return;
      activeSecondsRef.current = 0;
      api.post("/reading-progress", {
        module_id: mod.id,
        course_id: mod.course_id || null,
        active_seconds: secs,
      }).catch(() => {});
    };
    const tick = setInterval(() => {
      if (document.visibilityState === "visible" && Date.now() - lastActivityRef.current < 120000) {
        activeSecondsRef.current += 5;
      }
    }, 5000);
    const flusher = setInterval(flushDelta, 30000);
    const onHide = () => { if (document.visibilityState === "hidden") flushDelta(); };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      events.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener("visibilitychange", onHide);
      clearInterval(tick);
      clearInterval(flusher);
      flushDelta();
    };
  }, [mod?.id, mod?.course_id, isLink, stage]);

  const jumpToResume = () => {
    setShowResumeToast(false);
    if (isIframe) {
      const ifr = document.querySelector('iframe[data-testid="artifact-iframe"]');
      try {
        ifr?.contentWindow?.scrollTo({
          top: (ifr.contentDocument?.documentElement?.scrollHeight || 0) * (resumePct / 100),
          behavior: "smooth",
        });
      } catch (e) { /* sandbox may block — fine */ }
    } else if (scrollRef.current) {
      const el = scrollRef.current;
      const total = el.scrollHeight - el.clientHeight;
      el.scrollTo({ top: (resumePct / 100) * total, behavior: "smooth" });
    }
  };

  // Listen for iframe scroll progress (for embed/jsx)
  useEffect(() => {
    if (!isIframe) return undefined;
    const handler = (e) => {
      const d = e?.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "uvc-scroll" && typeof d.pct === "number") {
        setProgress(d.pct);
        if (d.pct >= 95 && !completed) setCompleted(true);
      }
      if (d.type === "uvc-sections-detected" && Array.isArray(d.sections)) {
        const built = d.sections
          .filter((s) => s && s.id && s.label)
          .map((s) => ({
            id: s.id,
            label: s.label.length > 60 ? s.label.slice(0, 58) + "…" : s.label,
            level: 2, // iframe-detected sections are flat
            el: null,
          }));
        setSections(built);
        if (!activeSection && built[0]) setActiveSection(built[0].id);
      }
      if (d.type === "uvc-section-seen" && d.id) {
        setSeenSections((prev) => {
          if (prev.has(d.id)) return prev;
          const next = new Set(prev);
          next.add(d.id);
          return next;
        });
        setActiveSection(d.id);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [isIframe, completed, activeSection]);

  // ===== Iframe interactive-marker bridge =====
  // The artifact iframe runs its own IntersectionObserver and posts
  // `uvc-interactive-show / -move / -hide` messages with rect coordinates
  // relative to the iframe viewport. We translate those into parent-page
  // coordinates, drop a hidden virtual anchor div there, and let
  // LearningCompanion glue Aria's bubble to it.
  useEffect(() => {
    if (!isIframe || stage !== "reading") return undefined;

    // One reusable virtual anchor div that's repositioned on every message.
    const anchor = document.createElement("div");
    anchor.setAttribute("data-uvc-virtual-anchor", "1");
    Object.assign(anchor.style, {
      position: "fixed",
      pointerEvents: "none",
      zIndex: "0",
      // Hidden but still has a measurable rect for getBoundingClientRect().
      opacity: "0",
      width: "1px",
      height: "1px",
    });
    document.body.appendChild(anchor);

    let currentId = null;

    const findIframeRect = () => {
      const ifr = document.querySelector('iframe[data-testid="artifact-iframe"]');
      return ifr ? ifr.getBoundingClientRect() : null;
    };

    const positionAnchor = (rect) => {
      const ir = findIframeRect();
      if (!ir) return false;
      // Clamp the rect into the iframe's visible area
      const left = ir.left + Math.max(0, Math.min(rect.left, ir.width));
      const top = ir.top + Math.max(0, Math.min(rect.top, ir.height));
      const width = Math.max(8, Math.min(rect.width, ir.width));
      const height = Math.max(8, Math.min(rect.height, ir.height));
      anchor.style.left = `${left}px`;
      anchor.style.top = `${top}px`;
      anchor.style.width = `${width}px`;
      anchor.style.height = `${height}px`;
      return true;
    };

    const handler = (e) => {
      const d = e?.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "uvc-interactive-show") {
        if (!positionAnchor(d.rect || { left: 0, top: 0, width: 200, height: 40 })) return;
        currentId = d.id;
        // Re-fire every viewport entry. Show for ~4s then auto-hide.
        ariaRef.current?.say(
          d.msg || "Interactive Feature Alert! Read carefully and tap along.",
          "encouraging",
          { hideAfter: 4000, holdMoodFor: 3000, anchorEl: anchor },
        );
      } else if (d.type === "uvc-interactive-move") {
        if (d.id !== currentId) return;
        positionAnchor(d.rect || {});
      } else if (d.type === "uvc-interactive-hide") {
        if (d.id === currentId) {
          currentId = null;
          ariaRef.current?.dismiss?.();
        }
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
      try { anchor.remove(); } catch { /* ignore */ }
    };
  }, [isIframe, stage, mod.id]);

  const jumpTo = (s) => {
    const el = s.el;
    if (el && scrollRef.current) {
      scrollRef.current.scrollTo({ top: el.offsetTop - 40, behavior: "smooth" });
    }
  };

  const fireConfetti = () => {
    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.6 },
      colors: ["#1E3A8A", "#F59E0B", "#10B981", "#3B82F6", "#FBBF24"],
    });
    setTimeout(() => confetti({ particleCount: 60, spread: 110, origin: { x: 0.2, y: 0.5 } }), 250);
    setTimeout(() => confetti({ particleCount: 60, spread: 110, origin: { x: 0.8, y: 0.5 } }), 400);
  };

  const onReadingDone = () => {
    if (!completed) setCompleted(true);
    fireConfetti();
    setTimeout(() => setStage("quiz"), 700);
  };

  if (stage === "quiz") {
    return (
      <>
        <QuizPlayer
          moduleId={mod.id}
          learner={learner}
          onBack={() => setStage("reading")}
          onFinished={() => setStage("result")}
          ariaRef={ariaRef}
        />
        <LearningCompanion ref={ariaRef} />
      </>
    );
  }

  if (stage === "result") {
    // Aria celebrates once on the result screen
    if (!milestonesHitRef.current.has("result")) {
      milestonesHitRef.current.add("result");
      setTimeout(() => ariaRef.current?.say(pickLine("quizPass"), "celebrating"), 400);
    }
    return (
      <>
        <ResultCelebration onClose={onClose} />
        <LearningCompanion ref={ariaRef} />
      </>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Sticky top progress bar */}
      <header className="border-b border-slate-200 bg-white shrink-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm" data-testid="reading-back">
            <ArrowLeft size={15}/> Exit
          </button>
          <a
            href={`/learn/forum?module=${mod.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reading-discuss-link"
            className="hidden sm:inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-[#E53935]/40 hover:text-[#E53935] transition-colors"
          >
            <MessageSquare size={11}/> Discuss
          </a>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <BookOpen size={13} className="text-blue-900 shrink-0"/>
                <span className="text-xs font-medium text-slate-700 truncate">{mod.title}</span>
              </div>
              <span className="text-xs font-mono text-slate-500 shrink-0">{progress.toFixed(0)}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-900 to-amber-400 transition-all duration-300"
                style={{ width: `${progress}%` }}
                data-testid="reading-progress-bar"
              />
            </div>
          </div>
        </div>

        {/* Section step indicator (option b) — dots fill green as ticked,
            click to jump. Shown for any module with 2+ detected sections. */}
        {sections.length > 1 && (
          <div
            className="border-t border-slate-100 max-w-5xl mx-auto px-6 py-2 overflow-x-auto thin-scroll"
            data-testid="section-step-indicator"
          >
            <div className="flex items-center gap-1.5 text-xs">
              {sections.map((s, i) => {
                const isActive = s.id === activeSection;
                const isDone = seenSections.has(s.id);
                return (
                  <React.Fragment key={s.id}>
                    {i > 0 && (
                      <span
                        className={`flex-1 h-0.5 min-w-[10px] transition-colors ${
                          isDone ? "bg-emerald-400" : "bg-slate-200"
                        }`}
                      />
                    )}
                    <button
                      onClick={() => jumpTo(s)}
                      data-testid={`section-step-${i}`}
                      title={s.label}
                      className={`group inline-flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                        isActive ? "text-[#E53935]" : isDone ? "text-emerald-700" : "text-slate-500"
                      }`}
                    >
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                          isDone
                            ? "bg-emerald-500 text-white"
                            : isActive
                              ? "bg-[#E53935] text-white ring-4 ring-[#E53935]/15"
                              : "bg-slate-100 text-slate-500 border border-slate-200"
                        }`}
                      >
                        {isDone ? <CheckCircle2 size={11} strokeWidth={3}/> : (i + 1)}
                      </span>
                      <span className={`hidden md:inline text-[11px] ${isActive ? "font-semibold" : isDone ? "font-medium" : ""} max-w-[160px] truncate`}>
                        {s.label}
                      </span>
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Floating right-side section ticker (option a) — collapsible. */}
      {sections.length > 1 && (
        <aside
          className={`fixed right-4 z-30 transition-all ${tickerOpen ? "w-72" : "w-12"}`}
          style={{ top: "50%", transform: "translateY(-50%)" }}
          data-testid="section-ticker"
        >
          {tickerOpen ? (
            <div className="bg-white border border-slate-200 rounded-xl shadow-[0_18px_50px_-15px_rgba(34,34,34,0.25)] overflow-hidden">
              <header className="px-3 py-2 bg-gradient-to-r from-[#222222] to-[#3a1212] text-white flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1.5">
                  <ListChecks size={11}/> Sections
                </span>
                <span className="text-[10px] text-white/80 font-mono">
                  {seenSections.size}/{sections.length}
                </span>
                <button
                  onClick={() => setTickerOpen(false)}
                  data-testid="section-ticker-collapse"
                  className="text-white/70 hover:text-white"
                  title="Collapse"
                >
                  <ChevronRight size={14}/>
                </button>
              </header>
              <ul className="max-h-[55vh] overflow-y-auto thin-scroll py-1">
                {sections.map((s, i) => {
                  const isDone = seenSections.has(s.id);
                  const isActive = s.id === activeSection;
                  return (
                    <li key={s.id}>
                      <button
                        onClick={() => jumpTo(s)}
                        data-testid={`section-tick-${i}`}
                        className={`w-full text-left flex items-start gap-2.5 px-3 py-2 text-[12px] transition-colors ${
                          isActive ? "bg-[#E53935]/8" : "hover:bg-slate-50"
                        }`}
                      >
                        <span
                          className={`mt-0.5 shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                            isDone
                              ? "bg-emerald-500 text-white"
                              : isActive
                                ? "border-2 border-[#E53935] bg-white"
                                : "border-2 border-slate-300 bg-white"
                          }`}
                        >
                          {isDone ? <CheckCircle2 size={9} strokeWidth={3}/> : null}
                        </span>
                        <span className={`leading-snug ${isDone ? "text-slate-400 line-through" : isActive ? "text-[#E53935] font-semibold" : "text-slate-700"}`}>
                          {s.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <footer className="px-3 py-1.5 bg-slate-50 border-t border-slate-100">
                <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: sections.length ? `${(seenSections.size / sections.length) * 100}%` : "0%" }}
                  />
                </div>
              </footer>
            </div>
          ) : (
            <button
              onClick={() => setTickerOpen(true)}
              data-testid="section-ticker-expand"
              className="w-12 h-12 rounded-full bg-[#222222] text-white shadow-lg hover:scale-105 transition-transform inline-flex items-center justify-center"
              title={`${seenSections.size}/${sections.length} sections`}
            >
              <ListChecks size={16}/>
              <span className="absolute -top-1 -right-1 bg-[#E53935] text-white text-[9px] rounded-full px-1.5 py-0.5 font-bold min-w-[18px] text-center">
                {seenSections.size}/{sections.length}
              </span>
            </button>
          )}
        </aside>
      )}

      {/* Resume toast — appears when learner returns to a module they've started */}
      {showResumeToast && resumePct >= 5 && resumePct < 95 && (
        <div
          data-testid="resume-toast"
          className="fixed top-24 right-4 z-40 max-w-xs bg-[#222222] text-white rounded-xl shadow-2xl border border-[#E53935]/30 overflow-hidden"
          style={{ animation: "resumeToastIn 0.4s ease-out" }}
        >
          <div className="px-4 py-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#E53935] font-bold mb-1">
              <RotateCcw size={11}/> Welcome back
            </div>
            <div className="text-sm font-semibold leading-snug mb-2">
              You were {Math.round(resumePct)}% through this module.
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={jumpToResume}
                data-testid="resume-jump-button"
                className="flex-1 bg-[#E53935] hover:bg-[#c5302d] text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
              >
                Resume reading
              </button>
              <button
                onClick={() => setShowResumeToast(false)}
                data-testid="resume-dismiss-button"
                className="text-white/60 hover:text-white text-xs px-2"
                title="Dismiss"
              >
                Start over
              </button>
            </div>
          </div>
          <style>{`@keyframes resumeToastIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </div>
      )}

      {/* Reading body */}
      <main ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll">
        {/* Hero banner — shown above prose AND iframe modules. */}
        {!isLink && mod.hero_image && (
          <figure
            data-testid="module-hero-image"
            className={`relative overflow-hidden bg-[#222222] ${isIframe
              ? "aspect-[16/5] sm:aspect-[16/4]"
              : "aspect-[16/7] max-w-3xl mx-auto md:rounded-xl md:mt-8 md:mb-10 shadow-[0_18px_60px_-20px_rgba(34,34,34,0.35)]"
            }`}
          >
            <img
              src={mod.hero_image}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ animation: "heroImgFade 0.7s ease-out" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#222222]/65 via-[#222222]/0 to-transparent" />
            <figcaption className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-3">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-semibold drop-shadow">
                UniVicoustic · Learning Module
              </span>
              <h2 className="text-white text-xl sm:text-2xl font-display font-bold drop-shadow-lg text-right line-clamp-2">
                {mod.title}
              </h2>
            </figcaption>
            <style>{`@keyframes heroImgFade { from { opacity: 0; transform: scale(1.04); } to { opacity: 1; transform: scale(1); } }`}</style>
          </figure>
        )}
        {isLink ? (
          <div className="h-full min-h-[80vh] max-w-5xl mx-auto px-6 py-6">
            <LinkArtifactView url={mod.artifact_content || ""} />
          </div>
        ) : isIframe ? (
          <div className="h-full min-h-[80vh]">
            <ArtifactIframe
              html={mod.artifact_content}
              mode={mod.artifact_type}
              viewport="desktop"
              moduleId={mod.id}
              learner={learner}
              trackScroll
            />
          </div>
        ) : (
          <div ref={proseRef} className="max-w-3xl mx-auto px-6 md:px-10 py-10 pb-32">
            {mod.artifact_type === "html"
              ? <div className="artifact-prose" dangerouslySetInnerHTML={{ __html: sanitizeProseHtml(mod.artifact_content) }} />
              : <MarkdownRender source={mod.artifact_content || ""} />}
          </div>
        )}
      </main>

      {/* Bottom action bar — appears when reading complete. BIG, hard-to-miss quiz CTA. */}
      <footer
        className={`shrink-0 transition-all ${completed ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 h-0 overflow-hidden"}`}
        data-testid="reading-complete-footer"
      >
        <div className="relative bg-gradient-to-r from-[#222222] via-[#2d0a0a] to-[#E53935] border-t-4 border-[#E53935] overflow-hidden">
          {/* Animated shimmer overlay */}
          <div className="absolute inset-0 opacity-30 pointer-events-none"
               style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)",
                        backgroundSize: "200% 100%", animation: "quizCtaShimmer 3s linear infinite" }} />
          <div className="relative max-w-3xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              {/* Big animated pointing-right finger */}
              <span aria-hidden className="text-3xl sm:text-4xl select-none"
                    style={{ animation: "quizCtaPoint 0.9s ease-in-out infinite" }}>
                👉
              </span>
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-[0.18em] text-white/70 font-semibold">Reading complete</span>
                <span className="text-base sm:text-lg font-semibold leading-tight">
                  Don&apos;t leave yet — <span className="text-white underline decoration-white/40 underline-offset-4">take the quiz</span> and lock it in!
                </span>
              </div>
            </div>
            <button
              onClick={onReadingDone}
              data-testid="start-quiz-button"
              className="relative group inline-flex items-center gap-2 bg-white text-[#E53935] hover:text-white hover:bg-[#222222] px-7 py-3 rounded-full text-base font-bold shadow-[0_6px_24px_rgba(229,57,53,0.55)] transition-all hover:scale-105"
              style={{ animation: "quizCtaPulse 1.6s ease-in-out infinite" }}
            >
              <Sparkles size={18} className="text-[#E53935] group-hover:text-white transition-colors"/>
              Start the Quiz
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform"/>
            </button>
          </div>
        </div>
        <style>{`
          @keyframes quizCtaShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
          @keyframes quizCtaPoint   { 0%,100% { transform: translateX(0) rotate(-3deg); } 50% { transform: translateX(8px) rotate(8deg); } }
          @keyframes quizCtaPulse   { 0%,100% { box-shadow: 0 6px 24px rgba(229,57,53,0.55); }
                                       50%    { box-shadow: 0 6px 36px rgba(229,57,53,0.95), 0 0 0 6px rgba(229,57,53,0.18); } }
        `}</style>
      </footer>

      {/* Floating "Done" button if scroll detection didn't trigger (for short pages or external links) */}
      {!completed && (isLink || progress > 30) && (
        <button
          onClick={() => setCompleted(true)}
          data-testid="mark-done-button"
          className="fixed bottom-6 right-6 bg-white border border-slate-300 hover:border-[#E53935] text-slate-700 hover:text-[#E53935] rounded-full px-4 py-2 text-xs font-medium shadow-md inline-flex items-center gap-1.5 transition-colors"
        >
          <Target size={12}/> I&apos;m done {isLink ? "viewing" : "reading"}
        </button>
      )}

      {/* Aria — the acoustic-wave learning companion */}
      <LearningCompanion ref={ariaRef} />
    </div>
  );
}

function QuizPlayer({ moduleId, learner, onBack, onFinished, ariaRef }) {
  const [stage, setStage] = useState("pre"); // pre | quiz | result
  const [meta, setMeta] = useState(null);    // {pass_threshold, max_attempts, attempts_remaining, best_percentage, passed, can_retake}
  const [questions, setQuestions] = useState(null); // shuffled questions with optionMap + originalQIdx
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});       // shuffled-q-idx -> shuffled-opt-idx
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // Anchor at the top of each question card — used to auto-scroll the
  // learner back to the question header whenever they advance/retreat.
  const questionTopRef = useRef(null);

  const learnerName = learner?.name || "";

  // Item 8 (iter 24): whenever the visible question changes — Next, Prev,
  // or any future auto-advance — bring the learner back to the top so the
  // new question header is fully visible. We scroll both the window and the
  // question anchor itself; one of them is guaranteed to land us at the top
  // regardless of the surrounding layout.
  useEffect(() => {
    if (stage !== "quiz") return;
    const t = requestAnimationFrame(() => {
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
      try { questionTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* noop */ }
    });
    return () => cancelAnimationFrame(t);
  }, [idx, stage]);

  // Load quiz + my-progress
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [q, mp] = await Promise.all([
          api.get(`/modules/${moduleId}/quiz`),
          api.get(`/modules/${moduleId}/my-progress`, { params: { learner_name: learnerName } }),
        ]);
        if (cancelled) return;
        setMeta(mp.data);
        if (q.data.has_quiz) {
          setQuestions(buildShuffled(q.data.quiz));
        } else {
          setQuestions([]);
        }
      } catch (e) {
        setQuestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [moduleId, learnerName]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-900" size={28}/>
      </div>
    );
  }

  // No quiz configured by admin
  if (!questions || questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <Card className="p-10 max-w-md text-center border-slate-200">
          <ListChecks className="mx-auto text-slate-300 mb-3" size={42} strokeWidth={1.3}/>
          <h2 className="text-xl font-display font-semibold text-slate-900 mb-2">No quiz available yet</h2>
          <p className="text-slate-500 text-sm mb-6">Your administrator hasn&apos;t generated a quiz for this module. Your reading completion has been noted.</p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={onBack}><ArrowLeft size={14} className="mr-1.5"/> Back to lesson</Button>
            <Button onClick={async () => {
              await api.post("/progress", {
                module_id: moduleId,
                learner_id: learner?.id || null,
                learner_name: learner?.name || "Anonymous",
                completed: true,
                details: { reading_complete: true, no_quiz: true },
              }).catch(() => {});
              onFinished();
            }} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="finish-no-quiz">
              Mark complete
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Pre-quiz briefing
  if (stage === "pre" && !result) {
    return <PreQuizScreen
      meta={meta}
      total={questions.length}
      onBack={onBack}
      onStart={() => { setAnswers({}); setIdx(0); setQuestions(buildShuffled(questions.map(q => unshuffle(q)))); setStage("quiz"); }}
      onSkip={onFinished}
    />;
  }

  const currentQ = questions[idx];
  const total = questions.length;
  const answered = Object.keys(answers).length;
  const pct = Math.round((answered / total) * 100);

  const choose = (oi) => {
    // Per user request (iter 24): no Aria reaction per option click — those
    // micro-reactions on every tap were distracting. We still emit Aria
    // praise once on overall result, in `submit()` below.
    setAnswers({ ...answers, [idx]: oi });
  };
  const next = () => { if (idx < total - 1) setIdx(idx + 1); };
  const prev = () => { if (idx > 0) setIdx(idx - 1); };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Build answers array in ORIGINAL question order, mapping shuffled option to original option
      const ordered = new Array(questions.length).fill(-1);
      questions.forEach((q, qIdx) => {
        const sel = answers[qIdx];
        if (sel != null && sel >= 0) ordered[q.originalQIdx] = q.optionMap[sel];
      });
      const { data } = await api.post(
        `/modules/${moduleId}/grade-quiz`,
        ordered,
        { params: { learner_name: learnerName } }
      );
      setResult(data);
      await api.post("/progress", {
        module_id: moduleId,
        learner_id: learner?.id || null,
        learner_name: learner?.name || "Anonymous",
        score: data.score,
        max_score: data.max_score,
        completed: true,
        details: { passed: data.passed, threshold: data.pass_threshold },
      });
      // Aria's reaction to the overall result
      if (ariaRef?.current) {
        ariaRef.current.say(pickLine(data.passed ? "quizPass" : "quizFail"), data.passed ? "celebrating" : "encouraging", { hideAfter: 9000, holdMoodFor: 6000 });
      }
      if (data.passed) {
        confetti({ particleCount: 180, spread: 95, origin: { y: 0.6 }, colors: ["#1E3A8A", "#F59E0B", "#10B981"] });
        setTimeout(() => confetti({ particleCount: 90, spread: 110, origin: { x: 0.1, y: 0.5 } }), 250);
        setTimeout(() => confetti({ particleCount: 90, spread: 110, origin: { x: 0.9, y: 0.5 } }), 400);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Submit failed";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <QuizResultView
        result={result}
        meta={meta}
        canRetake={!result.passed}
        onRetake={() => { setResult(null); setAnswers({}); setIdx(0); setQuestions(buildShuffled(questions.map(q => unshuffle(q)))); setStage("quiz"); }}
        onFinish={onFinished}
        learner={learner}
        moduleId={moduleId}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/50 flex flex-col" data-testid="quiz-mode-active">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={onBack} className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-1" data-testid="quiz-back-to-reading">
            <ArrowLeft size={14}/> Back
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
              <span className="font-medium inline-flex items-center gap-1.5"><ListChecks size={12}/> Quiz · Question {idx + 1} of {total}</span>
              <span className="font-mono">{answered}/{total} answered</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-900 to-amber-400 transition-all duration-500" style={{ width: `${pct}%` }} data-testid="quiz-progress-bar"/>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <Card ref={questionTopRef} className="max-w-2xl w-full p-8 md:p-10 border-slate-200 shadow-sm scroll-mt-24">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-900 font-semibold mb-3">Question {idx + 1}</div>
          <h2 className="text-2xl font-display font-bold text-slate-900 leading-snug mb-6" data-testid={`question-${idx}-text`}>
            {currentQ.q}
          </h2>
          <div className="space-y-3">
            {currentQ.options.map((opt, oi) => {
              const isSelected = answers[idx] === oi;
              return (
                <button
                  key={oi}
                  onClick={() => choose(oi)}
                  data-testid={`option-${idx}-${oi}`}
                  className={`w-full text-left px-5 py-4 rounded-lg border-2 transition-all flex items-center gap-3 ${
                    isSelected
                      ? "border-blue-900 bg-blue-50 text-blue-900"
                      : "border-slate-200 hover:border-slate-400 bg-white text-slate-700"
                  }`}
                >
                  <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0 ${
                    isSelected ? "border-blue-900 bg-blue-900 text-white" : "border-slate-300 text-slate-500"
                  }`}>{String.fromCharCode(65 + oi)}</span>
                  <span className="flex-1">{opt}</span>
                </button>
              );
            })}
          </div>
        </Card>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="outline" onClick={prev} disabled={idx === 0} data-testid="quiz-prev">
            <ArrowLeft size={14} className="mr-1.5"/> Previous
          </Button>
          {idx < total - 1 ? (
            <Button onClick={next} disabled={!(idx in answers)} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="quiz-next">
              Next <ArrowRight size={14} className="ml-1.5"/>
            </Button>
          ) : (
            <Button onClick={submit} disabled={answered < total || submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="quiz-submit">
              {submitting ? <><Loader2 size={14} className="animate-spin mr-1.5"/> Submitting</> : <><CheckCircle2 size={14} className="mr-1.5"/> Submit Quiz</>}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}

// =====================================================================
// Pre-quiz briefing screen — shows best score, threshold, attempts remaining
// =====================================================================
function PreQuizScreen({ meta, total, onBack, onStart, onSkip }) {
  const noRetake = !!meta && !meta.can_retake;
  const showBest = meta?.attempts > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-6">
      <div className="max-w-lg w-full">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-1 mb-4" data-testid="prequiz-back">
          <ArrowLeft size={14}/> Back to lesson
        </button>
        <Card className="p-8 md:p-10 border-slate-200 shadow-sm" data-testid="prequiz-screen">
          <div className="flex items-center gap-2 text-blue-900 mb-2">
            <ListChecks size={18}/>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold">Knowledge Check</span>
          </div>
          <h2 className="text-3xl font-display font-bold text-slate-900 mb-4">Ready when you are.</h2>

          <div className="grid grid-cols-2 gap-3 mb-6">
            <Stat label="Questions" value={total} />
            <Stat label="Pass mark" value={`${meta?.pass_threshold ?? 70}%`} accent="text-amber-700" />
            {showBest && (
              <Stat
                label="Your best score"
                value={meta.best_percentage != null ? `${meta.best_percentage.toFixed(0)}%` : "—"}
                accent={meta.passed ? "text-emerald-700" : "text-slate-900"}
              />
            )}
          </div>

          {/* Status banners */}
          {meta?.passed && (
            <div className="mb-5 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm flex items-start gap-2" data-testid="prequiz-passed-banner">
              <Trophy size={16} className="shrink-0 mt-0.5"/>
              <div>You&apos;ve already passed this quiz (best: {meta.best_percentage?.toFixed(0)}%). Take it again any time to improve your score.</div>
            </div>
          )}
          {!meta?.passed && showBest && (
            <div className="mb-5 p-3 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-sm flex items-start gap-2" data-testid="prequiz-retake-banner">
              <RotateCcw size={16} className="shrink-0 mt-0.5"/>
              <div>
                Your last attempt scored <strong>{meta.best_percentage?.toFixed(0)}%</strong>. You need <strong>{(meta.pass_threshold || 70)}%</strong> to pass.
              </div>
            </div>
          )}

          {/* Tips */}
          <ul className="text-xs text-slate-500 space-y-1.5 mb-7 list-disc pl-5">
            <li>Question and answer order will be randomized each attempt.</li>
            <li>You can&apos;t see the lesson while the quiz is in progress.</li>
            <li>Your score is saved automatically when you submit.</li>
          </ul>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onSkip} data-testid="prequiz-skip" className="flex-1">
              Maybe later
            </Button>
            <Button
              onClick={onStart}
              disabled={noRetake}
              data-testid="prequiz-start"
              className="flex-1 bg-blue-900 hover:bg-blue-800 text-white disabled:opacity-50"
            >
              {showBest ? <><RotateCcw size={14} className="mr-1.5"/> Retake quiz</> : <><Sparkles size={14} className="mr-1.5"/> Start quiz</>}
              <ArrowRight size={14} className="ml-1.5"/>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-slate-900" }) {
  return (
    <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
      <div className={`text-2xl font-display font-bold ${accent}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}

// =====================================================================
// Quiz result view — extracted so retake & finish callbacks can be wired cleanly
// =====================================================================
function QuizResultView({ result, meta, canRetake, onRetake, onFinish, learner, moduleId }) {
  const passed = result.passed;
  const downloadCertificate = async () => {
    try {
      const res = await api.get(`/certificate/${moduleId}`, {
        params: { learner_name: learner?.name || "Anonymous" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `UniVicoustic-Certificate-${(learner?.name || "Anonymous").replace(/\s/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error("Failed to download certificate");
    }
  };
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className={`p-8 mb-6 text-center border-2 ${passed ? "border-emerald-400 bg-emerald-50/50" : "border-amber-400 bg-amber-50/30"}`}>
          <Trophy className={`mx-auto mb-3 ${passed ? "text-amber-500" : "text-slate-400"}`} size={48} strokeWidth={1.5}/>
          <div className="text-5xl font-display font-extrabold text-slate-900 mb-1" data-testid="quiz-score-display">{result.score}/{result.max_score}</div>
          <div className="text-lg text-slate-700 mb-2">{result.percentage}%</div>
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
            passed ? "bg-emerald-600 text-white" : "bg-amber-100 text-amber-900"
          }`} data-testid={passed ? "result-passed" : "result-failed"}>
            {passed ? <><CheckCircle2 size={12}/> Passed · ≥ {result.pass_threshold}%</> : <><XCircle size={12}/> Below {result.pass_threshold}% · Keep going</>}
          </div>
          {passed && (
            <button
              onClick={downloadCertificate}
              data-testid="download-certificate-button"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-[#222222] hover:bg-black text-white rounded-md text-sm font-medium transition-colors"
            >
              <Trophy size={14}/> Download Certificate (PDF)
            </button>
          )}
        </Card>

        <div className="space-y-3">
          {result.results.map((r, i) => (
            <Card key={i} className="p-5 border-slate-200" data-testid={`quiz-result-${i}`}>
              <div className="flex items-start gap-3 mb-3">
                {r.is_right
                  ? <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={18}/>
                  : <XCircle className="text-red-500 shrink-0 mt-0.5" size={18}/>}
                <div className="font-medium text-slate-900 text-sm flex-1">{i + 1}. {r.q}</div>
              </div>
              <div className="space-y-1.5 ml-7">
                {r.options.map((opt, oi) => (
                  <div key={oi} className={`text-sm px-3 py-1.5 rounded ${
                    oi === r.correct_idx ? "bg-emerald-100 text-emerald-900 font-medium"
                    : oi === r.your_choice ? "bg-red-100 text-red-900"
                    : "text-slate-500"
                  }`}>
                    {oi === r.correct_idx && "✓ "}
                    {oi === r.your_choice && oi !== r.correct_idx && "✗ "}
                    {opt}
                  </div>
                ))}
              </div>
              {r.explanation && (
                <div className="ml-7 mt-3 text-xs text-slate-600 italic border-l-2 border-slate-200 pl-3">{r.explanation}</div>
              )}
            </Card>
          ))}
        </div>

        <div className="flex justify-between mt-6">
          {canRetake ? (
            <Button variant="outline" onClick={onRetake} data-testid="retake-quiz-button">
              <RotateCcw size={14} className="mr-1.5"/> Retake
            </Button>
          ) : <div />}
          <Button onClick={onFinish} className="bg-[#222222] hover:bg-black text-white" data-testid="finish-quiz-button">
            Finish <ArrowRight size={14} className="ml-1.5"/>
          </Button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// ResultCelebration — final "Module complete!" screen after the quiz.
// Counts down from 6 s and auto-navigates the learner back to the modules
// listing (the "learning modules section"). Admin requested this because
// users were getting stuck on this screen waiting for something to happen.
// They can also click "Back to modules" to skip the countdown.
// =====================================================================
function ResultCelebration({ onClose }) {
  const [seconds, setSeconds] = React.useState(6);
  React.useEffect(() => {
    const tick = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    const goto = setTimeout(() => onClose?.(), 6000);
    return () => { clearInterval(tick); clearTimeout(goto); };
  }, [onClose]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="quiz-result-celebration">
      <Card className="p-10 max-w-md text-center border-slate-200">
        <Trophy className="mx-auto text-amber-500 mb-4" size={48} strokeWidth={1.5}/>
        <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">Module complete!</h2>
        <p className="text-slate-500 mb-6">Your score has been recorded. Returning to your modules in <strong>{seconds}s</strong>.</p>
        <Button onClick={onClose} className="bg-[#222222] hover:bg-black text-white" data-testid="result-back-to-modules">
          <ArrowLeft size={14} className="mr-1.5"/> Back to modules
        </Button>
      </Card>
    </div>
  );
}

// =====================================================================
// Shuffle helpers — randomize question order + option order per attempt.
// Preserves mapping back to ORIGINAL indices so the backend grader works unchanged.
// =====================================================================
function shuffleArray(arr) {
  // Intentional no-op: questions and options must appear in the exact order
  // defined by the admin (e.g. the Word file order). Kept as a passthrough so
  // we don't have to rewrite the callers that map through optionMap.
  return arr.slice();
}

function buildShuffled(quiz) {
  // No reshuffle — preserve the admin-defined question + option order verbatim.
  // We still attach originalQIdx + identity optionMap so the existing scoring
  // / retake logic that destructures these fields keeps working.
  return quiz.map((q, i) => ({
    originalQIdx: q.originalQIdx ?? i,
    q: q.q,
    options: q.options.slice(),
    optionMap: q.options.map((_, oi) => oi), // identity mapping
  }));
}

// Reverse a shuffled question back to its original form so we can re-shuffle on retake
function unshuffle(shuffledQ) {
  const origOptions = new Array(shuffledQ.options.length);
  shuffledQ.optionMap.forEach((origIdx, displayIdx) => {
    origOptions[origIdx] = shuffledQ.options[displayIdx];
  });
  return { q: shuffledQ.q, options: origOptions, originalQIdx: shuffledQ.originalQIdx };
}
